import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Tab,
    Tabs,
    Typography,
    Card,
    Divider,
    CircularProgress,
    Alert,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Grid,
    Autocomplete,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Paper
} from '@mui/material';
import {
    Add as AddIcon,
    Warning as WarningIcon,
    List as ListIcon,
    AttachMoney as MoneyIcon,
    Visibility as ViewIcon,
    Description as ExcelIcon,
    PictureAsPdf as PdfIcon,
    Close as CloseIcon,
    Delete as DeleteIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { purchaseOrdersAPI, VendorPricingBulkRequest, PriceHistoryResponse, VendorPricingSummary, ActivePriceResponse, VendorPriceBulkItem } from '../../../api/purchaseOrders';
import { zohoVendorAPI } from '../../../api/zohoVendor';
import { zohoItemAPI } from '../../../api/zohoItem';
import { formatDateForDisplay, getDaysUntil, getTodayISO } from '../../../utils/dateUtils';
import { formatDate } from '../../../utils/formatters';
import useAuthStore from '../../../store/authStore';
import API from '../../../api/client';

interface VendorOption {
    id: number;
    name: string;
}

interface ItemOption {
    id: number;
    name: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vendor-pricing-tabpanel-${index}`}
            aria-labelledby={`vendor-pricing-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const VendorPricingManager: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuthStore();

    const [vendors, setVendors] = useState<VendorOption[]>([]);
    const [items, setItems] = useState<ItemOption[]>([]);
    const [priceHistory, setPriceHistory] = useState<PriceHistoryResponse[]>([]);
    const [vendorSummary, setVendorSummary] = useState<VendorPricingSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);

    // Tab Interface State
    const [tabValue, setTabValue] = useState(0);

    // Bulk Form state
    const [selectedVendor, setSelectedVendor] = useState<VendorOption | null>(null);
    const [effectiveFrom, setEffectiveFrom] = useState<string>(getTodayISO());
    const [effectiveTo, setEffectiveTo] = useState<string>('');
    const [generalNotes, setGeneralNotes] = useState('');

    // Multi-item list
    const [bulkItems, setBulkItems] = useState<any[]>([]);

    // Current item entry
    const [currentItem, setCurrentItem] = useState<ItemOption | null>(null);
    const [currentPrice, setCurrentPrice] = useState<string>('');
    const [currentItemNotes, setCurrentItemNotes] = useState('');

    // Filter state
    const [filterVendor, setFilterVendor] = useState<VendorOption | null>(null);
    const [filterItem, setFilterItem] = useState<ItemOption | null>(null);

    // View Dialog State
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewVendor, setViewVendor] = useState<VendorPricingSummary | null>(null);
    const [activePrices, setActivePrices] = useState<ActivePriceResponse[]>([]);
    const [viewLoading, setViewLoading] = useState(false);

    // Check if user is admin
    const isAdmin = user?.role === 'Admin';

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Handle View Dialog
    const handleView = async (vendor: VendorPricingSummary) => {
        setViewVendor(vendor);
        setViewDialogOpen(true);
        setViewLoading(true);
        try {
            // Use internal ID
            const prices = await purchaseOrdersAPI.getActivePrices(vendor.id);
            setActivePrices(prices);
        } catch (error: any) {
            console.error('Failed to load active prices:', error);
            enqueueSnackbar('Failed to load active prices', { variant: 'error' });
        } finally {
            setViewLoading(false);
        }
    };

    const handleCloseViewDialog = () => {
        setViewDialogOpen(false);
        setViewVendor(null);
        setActivePrices([]);
    };

    const handleDownloadExcel = async () => {
        if (!viewVendor) return;
        try {
            const response = await API.get(
                `/purchase-orders/vendor-pricing/${viewVendor.contact_id}/export/excel`,
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `vendor_pricing_${viewVendor.contact_id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Download failed:', error);
            enqueueSnackbar('Failed to download Excel file', { variant: 'error' });
        }
    };

    const handleDownloadPDF = async () => {
        if (!viewVendor) return;
        try {
            const response = await API.get(
                `/purchase-orders/vendor-pricing/${viewVendor.contact_id}/export/pdf`,
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `vendor_pricing_${viewVendor.contact_id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Download failed:', error);
            enqueueSnackbar('Failed to download PDF file', { variant: 'error' });
        }
    };

    // Load vendors and items
    useEffect(() => {
        const loadData = async () => {
            try {
                const [vendorsData, itemsData] = await Promise.all([
                    zohoVendorAPI.getItems(),
                    zohoItemAPI.getItems(),
                ]);

                setVendors(vendorsData.vendors?.map((v: any) => ({ id: v.id, name: v.contact_name })) || []);
                setItems(itemsData.items?.map((i: any) => ({ id: i.id, name: i.name })) || []);
            } catch (error: any) {
                console.error('Failed to load data:', error);
                enqueueSnackbar('Failed to load vendors and items', { variant: 'error' });
            }
        };

        loadData();
    }, [enqueueSnackbar]);

    // Load vendor pricing summary
    const loadSummary = async () => {
        setSummaryLoading(true);
        try {
            const summary = await purchaseOrdersAPI.getVendorsWithPricing();
            setVendorSummary(summary);
        } catch (error: any) {
            console.error('Failed to load vendor summary:', error);
        } finally {
            setSummaryLoading(false);
        }
    };

    useEffect(() => {
        loadSummary();
    }, []);

    useEffect(() => {
        if (tabValue === 0) {
            loadSummary();
        }
    }, [tabValue]);

    // Load price history when tab changes or filter changes
    useEffect(() => {
        const loadHistory = async () => {
            if (!filterVendor) return;
            setLoading(true);
            try {
                // Ensure we pass the internal ID to the API
                const history = await purchaseOrdersAPI.getPriceHistory(
                    filterVendor.id,
                    filterItem?.id
                );
                setPriceHistory(history);
            } catch (error: any) {
                console.error('Failed to load price history:', error);
                enqueueSnackbar('Failed to load price history', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };

        if (tabValue === 2) {
            loadHistory();
        }
    }, [filterVendor, filterItem, tabValue, enqueueSnackbar]);

    const handleAddItem = () => {
        if (!currentItem || !currentPrice) {
            enqueueSnackbar('Please select an item and enter a price', { variant: 'error' });
            return;
        }

        if (bulkItems.find(i => i.item_id === currentItem.id)) {
            enqueueSnackbar('Item already added to list', { variant: 'warning' });
            return;
        }

        setBulkItems([
            ...bulkItems,
            {
                item_id: currentItem.id,
                item_name: currentItem.name,
                price: parseFloat(currentPrice),
                notes: currentItemNotes
            }
        ]);

        // Reset item entry
        setCurrentItem(null);
        setCurrentPrice('');
        setCurrentItemNotes('');
    };

    const handleRemoveItem = (index: number) => {
        setBulkItems(bulkItems.filter((_, i) => i !== index));
    };

    const handleSubmitBulk = async () => {
        if (!selectedVendor || !effectiveFrom || bulkItems.length === 0) {
            enqueueSnackbar('Please fill required fields and add at least one item', { variant: 'error' });
            return;
        }

        setLoading(true);
        try {
            const request: VendorPricingBulkRequest = {
                vendor_id: selectedVendor.id,
                effective_from: effectiveFrom,
                effective_to: effectiveTo || undefined,
                notes: generalNotes || undefined,
                items: bulkItems.map(i => ({
                    item_id: i.item_id,
                    price: i.price,
                    notes: i.notes || undefined
                }))
            };

            await purchaseOrdersAPI.addVendorPriceBulk(request);
            enqueueSnackbar(`Successfully updated pricing for ${bulkItems.length} items`, { variant: 'success' });

            // Reset form
            setBulkItems([]);
            setGeneralNotes('');
            setEffectiveTo('');

            // Move to history
            setFilterVendor(selectedVendor);
            setTabValue(2);
        } catch (error: any) {
            console.error('Failed to add bulk pricing:', error);
            enqueueSnackbar(error.response?.data?.detail || 'Failed to add pricing', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    if (!isAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Access Denied: This page is only accessible to administrators.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Vendor Pricing Management
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab icon={<ListIcon />} iconPosition="start" label="Vendor Overview" />
                    <Tab icon={<AddIcon />} iconPosition="start" label="Add Pricing" />
                    <Tab icon={<HistoryIcon />} iconPosition="start" label="Price History" />
                </Tabs>
            </Box>

            {/* TAB 1: Vendor Overview */}
            <CustomTabPanel value={tabValue} index={0}>
                <Card sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Vendors with Pricing Configured</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setTabValue(1)}>
                            Add New Prices
                        </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {summaryLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                    ) : vendorSummary.length === 0 ? (
                        <Alert severity="info">No vendors with pricing configured. Go to 'Add Pricing' to start.</Alert>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Vendor Name</TableCell>
                                    <TableCell>Company</TableCell>
                                    <TableCell align="right">Items with Pricing</TableCell>
                                    <TableCell>Last Update</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vendorSummary.map((vendor) => (
                                    <TableRow key={vendor.id} hover>
                                        <TableCell>{vendor.contact_name}</TableCell>
                                        <TableCell>{vendor.company_name || '-'}</TableCell>
                                        <TableCell align="right"><Chip label={vendor.items_count} size="small" color="primary" /></TableCell>
                                        <TableCell>{formatDate(vendor.last_price_update)}</TableCell>
                                        <TableCell align="right">
                                            <Button size="small" startIcon={<ViewIcon />} onClick={() => handleView(vendor)} sx={{ mr: 1 }} variant="outlined">
                                                View
                                            </Button>
                                            <Button size="small" onClick={() => {
                                                // vendor.id is already the internal ID from vendorSummary
                                                setSelectedVendor({ id: vendor.id, name: vendor.contact_name });
                                                setTabValue(1);
                                            }}>
                                                Manage
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Card>
            </CustomTabPanel>

            {/* TAB 2: Add Pricing (Bulk) */}
            <CustomTabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                    {/* Setup Section */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" gutterBottom color="primary">1. Pricing Setup</Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Autocomplete
                                options={vendors}
                                getOptionLabel={(option) => option.name}
                                value={selectedVendor}
                                onChange={(_, v) => setSelectedVendor(v)}
                                renderInput={(params) => <TextField {...params} label="Select Vendor *" required />}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                label="Effective From *"
                                type="date"
                                value={effectiveFrom}
                                onChange={(e) => setEffectiveFrom(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                required
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                label="Effective To (Optional)"
                                type="date"
                                value={effectiveTo}
                                onChange={(e) => setEffectiveTo(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                sx={{ mb: 2 }}
                                helperText="Leave empty for indefinite validity"
                            />

                            <TextField
                                label="General Notes"
                                multiline
                                rows={2}
                                value={generalNotes}
                                onChange={(e) => setGeneralNotes(e.target.value)}
                                fullWidth
                                placeholder="e.g., Seasonal Adjustments"
                            />
                        </Card>
                    </Grid>

                    {/* Entry Section */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Typography variant="h6" gutterBottom color="primary">2. Add Items</Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    options={items}
                                    getOptionLabel={(option) => option.name}
                                    value={currentItem}
                                    onChange={(_, v) => setCurrentItem(v)}
                                    renderInput={(params) => <TextField {...params} label="Item Name *" />}
                                    sx={{ flex: 2 }}
                                />
                                <TextField
                                    label="Price (₹) *"
                                    type="number"
                                    value={currentPrice}
                                    onChange={(e) => setCurrentPrice(e.target.value)}
                                    sx={{ flex: 1 }}
                                    inputProps={{ step: 0.01 }}
                                />
                                <TextField
                                    label="Item Notes"
                                    value={currentItemNotes}
                                    onChange={(e) => setCurrentItemNotes(e.target.value)}
                                    sx={{ flex: 1.5 }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddItem}
                                    sx={{ height: 56 }}
                                    startIcon={<AddIcon />}
                                >
                                    Add
                                </Button>
                            </Box>

                            <Paper variant="outlined" sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Price (₹)</TableCell>
                                            <TableCell>Notes</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {bulkItems.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                    No items added yet. Use the form above to build your list.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            bulkItems.map((item, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{item.item_name}</TableCell>
                                                    <TableCell align="right">₹{item.price.toFixed(2)}</TableCell>
                                                    <TableCell>{item.notes || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveItem(idx)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button variant="outlined" onClick={() => setBulkItems([])}>Clear All</Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="large"
                                    disabled={loading || bulkItems.length === 0}
                                    onClick={handleSubmitBulk}
                                    startIcon={loading ? <CircularProgress size={20} /> : <MoneyIcon />}
                                >
                                    Save All Prices
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </CustomTabPanel>

            {/* TAB 3: Price History */}
            <CustomTabPanel value={tabValue} index={2}>
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Pricing History & Logs</Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <Autocomplete
                            options={vendorSummary}
                            getOptionLabel={(option: any) => option.contact_name}
                            value={filterVendor ? vendorSummary.find(v => v.id === filterVendor.id) : null}
                            onChange={(_, v: any) => {
                                if (v) {
                                    setFilterVendor({ id: v.id, name: v.contact_name });
                                } else {
                                    setFilterVendor(null);
                                }
                            }}
                            renderInput={(params) => <TextField {...params} label="Filter by Vendor (With Pricing)" />}
                            sx={{ flex: 1 }}
                        />
                        <Autocomplete
                            options={items}
                            getOptionLabel={(option) => option.name}
                            value={filterItem}
                            onChange={(_, v) => setFilterItem(v)}
                            renderInput={(params) => <TextField {...params} label="Filter by Item" />}
                            sx={{ flex: 1 }}
                        />
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                    ) : priceHistory.length === 0 ? (
                        <Alert severity="info">
                            {filterVendor ? 'No pricing history found for selection.' : 'Select a vendor to view history.'}
                        </Alert>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Vendor</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Price</TableCell>
                                    <TableCell>Validity</TableCell>
                                    <TableCell>Updated By</TableCell>
                                    <TableCell>Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {priceHistory.map((entry) => (
                                    <TableRow key={entry.id}>
                                        <TableCell>{entry.vendor_name}</TableCell>
                                        <TableCell>{entry.item_name}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>₹{Number(entry.price).toFixed(2)}</TableCell>
                                        <TableCell>
                                            {formatDateForDisplay(entry.effective_from)}
                                            {entry.effective_to ? ` to ${formatDateForDisplay(entry.effective_to)}` : ' (Full)'}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" display="block">{entry.created_by || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(entry.created_at)}</Typography>
                                        </TableCell>
                                        <TableCell>{entry.notes || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Card>
            </CustomTabPanel>

            {/* View Dialog */}
            <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Current Price List: {viewVendor?.contact_name}</Typography>
                        <IconButton onClick={handleCloseViewDialog}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {viewLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item Name</TableCell>
                                    <TableCell align="right">Active Price</TableCell>
                                    <TableCell>Validity Period</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {activePrices.map((price, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{price.item_name}</TableCell>
                                        <TableCell align="right">₹{Number(price.price).toFixed(2)}</TableCell>
                                        <TableCell>
                                            {formatDateForDisplay(price.effective_from || '')}
                                            {price.effective_to ? ` - ${formatDateForDisplay(price.effective_to)}` : ' onwards'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button startIcon={<ExcelIcon />} color="success" onClick={handleDownloadExcel}>Excel</Button>
                    <Button startIcon={<PdfIcon />} color="error" onClick={handleDownloadPDF}>PDF</Button>
                    <Button onClick={handleCloseViewDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VendorPricingManager;
